import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Archive, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { LOGOS } from './brandKitData';
import { KIT, Section, Logos, Palette, Typography, Voice } from './BrandKitParts';
import { SharePanel } from './SharePanel';
import './brandkit.css';

const FONT_LINKS = [
  'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600&display=swap',
  'https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@700,800&display=swap',
];

export const useBrandFonts = () => useEffect(() => {
  const els = FONT_LINKS.map((href) => {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = href;
    document.head.appendChild(l);
    return l;
  });
  return () => els.forEach((l) => l.remove());
}, []);

const ZipButton = () => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const root = zip.folder('AudiNexa-Brand-Kit');
      const files = LOGOS.flatMap((l) => [['PNG', l.png], ['PNG', l.extra], ['SVG', l.svg], ['SVG/embedded-png', l.embedded]])
        .filter(([, f]) => f);
      for (const [dir, f] of files) {
        const r = await fetch(`${KIT}/${f}`);
        if (!r.ok) throw new Error(f);
        root.folder(dir).file(f.replace('-embedded', ''), await r.blob());
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'AudiNexa-Brand-Kit.zip';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      toast.success('Brand kit downloaded');
    } catch (e) {
      toast.error('Could not build the brand kit');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="bk-btn bk-btn-primary" onClick={run} disabled={busy} data-testid="brandkit-download-zip-btn">
      {busy ? <Loader2 size={16} className="animate-spin" /> : <Archive size={16} />}
      {busy ? 'Packaging…' : 'Download Brand Kit (ZIP)'}
    </button>
  );
};

export const BrandKitContent = () => (
  <main className="bk-page">
    <div className="bk-hero">
      <img src={`${KIT}/audinexa-logo-primary.svg`} alt="AudiNexa" />
      <div><h1>Brand Kit</h1><p>Logos, colours, typography and voice for AudiNexa. Version 1.0 · {new Date().getFullYear()}</p></div>
    </div>
    <Section id="logos" no="01" title="Logo variations"><Logos /></Section>
    <Section id="colours" no="02" title="Colour palette"><Palette /></Section>
    <Section id="typography" no="03" title="Typography"><Typography /></Section>
    <Section id="voice" no="04" title="Brand voice"><Voice /></Section>
    <Section id="downloads" no="05" title="Downloadable assets">
      <div className="bk-downloads">
        <p>All three logos as high-resolution PNG and as SVG: a redrawn vector version that scales to any size, plus an SVG
          with the original PNG inside for tools that need the exact original pixels.</p>
        <ZipButton />
      </div>
    </Section>
  </main>
);

export default function BrandKitPage() {
  const { user } = useAuth();
  useBrandFonts();
  useEffect(() => { document.title = 'AudiNexa — Brand Kit'; }, []);
  if (user?.role !== 'founder') return <Navigate to="/" replace />;
  return (
    <div className="bk-root" data-testid="brand-kit-page">
      <header className="bk-toolbar">
        <Link to="/admin/dashboard" className="bk-btn" data-testid="brandkit-back-link"><ArrowLeft size={15} /> Admin</Link>
        <div className="bk-toolbar-right">
          <SharePanel />
          <button type="button" className="bk-btn" onClick={() => window.print()} data-testid="brandkit-print-btn">
            <Printer size={15} /> Print
          </button>
        </div>
      </header>
      <BrandKitContent />
    </div>
  );
}
